// frontend/src/features/tasks/EditCompletionModal.tsx
/**
 * Modal for editing an existing task completion.
 * Shows read-only task info (name, type, equipment) with editable
 * completion date/time and notes fields, plus delete with confirmation.
 */
import { useEffect, useState } from 'react';
import { Modal, Drawer, Stack, Text, Textarea, Group, Button, Alert, Divider } from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { IconAlertCircle, IconArrowBackUp } from '@tabler/icons-react';
import type { TaskCompletion } from '@/types/task';
import { TASK_TYPE_LABELS } from '@/types/task';
import { useIsMobile } from '@/hooks/useIsMobile';

interface EditCompletionModalProps {
  opened: boolean;
  onClose: () => void;
  completion: TaskCompletion | null;
  onSave: (completionId: string, notes: string, completedAt: string) => void;
  onDelete: (completionId: string) => void;
  onUndo?: (completionId: string) => void;
  isLoading?: boolean;
}

export function EditCompletionModal({
  opened,
  onClose,
  completion,
  onSave,
  onDelete,
  onUndo,
  isLoading = false,
}: EditCompletionModalProps) {
  const isMobile = useIsMobile();
  const [notes, setNotes] = useState('');
  const [completedAt, setCompletedAt] = useState<Date | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showUndoConfirm, setShowUndoConfirm] = useState(false);

  // Update notes when completion changes or modal opens
  useEffect(() => {
    if (completion && opened) {
      setNotes(completion.notes || '');
      setCompletedAt(new Date(completion.completed_at));
      setShowDeleteConfirm(false);
      setShowUndoConfirm(false);
    }
  }, [completion, opened]);

  const handleSave = () => {
    if (completion && completedAt) {
      onSave(completion.id, notes, completedAt.toISOString());
    }
  };

  const handleDelete = () => {
    if (completion) {
      onDelete(completion.id);
    }
  };

  const handleUndo = () => {
    if (completion && onUndo) {
      onUndo(completion.id);
    }
  };

  const handleClose = () => {
    setShowDeleteConfirm(false);
    setShowUndoConfirm(false);
    onClose();
  };

  if (!completion) return null;

  const content = (
    <Stack gap="md">
      {/* Read-only info */}
      <div>
        <Text size="sm" c="dimmed" mb={4}>Task</Text>
        <Text fw={500}>{completion.name || 'Unknown Task'}</Text>
      </div>

      <div>
        <Text size="sm" c="dimmed" mb={4}>Type</Text>
        <Text fw={500}>
          {completion.task_type ? TASK_TYPE_LABELS[completion.task_type] : 'Unknown'}
        </Text>
      </div>

      <DateTimePicker
        label="Completed"
        value={completedAt}
        onChange={(val) => { if (val) setCompletedAt(new Date(val)); }}
        maxDate={new Date()}
        required
      />

      {completion.equipment_name && (
        <div>
          <Text size="sm" c="dimmed" mb={4}>Equipment</Text>
          <Text fw={500}>{completion.equipment_name}</Text>
        </div>
      )}

      {/* Editable notes */}
      <Textarea
        label="Notes"
        placeholder="Add notes about this completion..."
        value={notes}
        onChange={(e) => setNotes(e.currentTarget.value)}
        minRows={4}
        maxRows={8}
      />

      {/* Undo confirmation */}
      {showUndoConfirm && (
        <Alert
          icon={<IconArrowBackUp size={16} />}
          color="blue"
          variant="light"
        >
          <Text size="sm" mb="xs">
            Move this task back to your due list? The completion record will be removed.
          </Text>
          <Group gap="xs" mt="xs">
            <Button
              size="xs"
              color="blue"
              onClick={handleUndo}
              loading={isLoading}
            >
              Yes, Mark as Undone
            </Button>
            <Button
              size="xs"
              variant="subtle"
              onClick={() => setShowUndoConfirm(false)}
            >
              Cancel
            </Button>
          </Group>
        </Alert>
      )}

      {/* Delete confirmation */}
      {showDeleteConfirm && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          variant="light"
        >
          <Text size="sm" mb="xs">
            Are you sure you want to delete this completion? This action cannot be undone.
          </Text>
          <Group gap="xs" mt="xs">
            <Button
              size="xs"
              color="red"
              onClick={handleDelete}
              loading={isLoading}
            >
              Delete Permanently
            </Button>
            <Button
              size="xs"
              variant="subtle"
              onClick={() => setShowDeleteConfirm(false)}
            >
              Cancel
            </Button>
          </Group>
        </Alert>
      )}

      <Divider />

      {/* Actions */}
      <Group justify="space-between">
        <Group gap="xs">
          {onUndo && (
            <Button
              variant="subtle"
              color="blue"
              leftSection={<IconArrowBackUp size={16} />}
              onClick={() => { setShowUndoConfirm(true); setShowDeleteConfirm(false); }}
              disabled={showUndoConfirm}
            >
              Mark as Undone
            </Button>
          )}
          <Button
            variant="subtle"
            color="red"
            onClick={() => { setShowDeleteConfirm(true); setShowUndoConfirm(false); }}
            disabled={showDeleteConfirm}
          >
            Delete
          </Button>
        </Group>
        <Group gap="xs">
          <Button variant="subtle" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={isLoading}>
            Save Changes
          </Button>
        </Group>
      </Group>
    </Stack>
  );

  return isMobile ? (
    <Drawer
      opened={opened}
      onClose={handleClose}
      title="Edit Task Completion"
      position="bottom"
      size="85%"
      styles={{
        body: {
          paddingBottom: 'env(safe-area-inset-bottom, 16px)',
        },
      }}
    >
      {content}
    </Drawer>
  ) : (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Edit Task Completion"
      size="md"
    >
      {content}
    </Modal>
  );
}
